import type { MouseEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { MembershipStandardNudgeTooltipContent } from './MembershipStandardNudgeTooltipContent';

import { InfoCircle10x10Icon } from '~/assets/icons';

type Position = { top: number };

type Props = {
  offsetY?: number;
};

const DEFAULT_OFFSET_Y = 28;

const stopEvent = (e: MouseEvent) => {
  e.stopPropagation();
  e.preventDefault();
};

const computePosition = (trigger: HTMLElement, offsetY: number): Position => {
  const triggerRect = trigger.getBoundingClientRect();
  return {
    top: triggerRect.bottom + window.scrollY + offsetY,
  };
};

export function MembershipStandardNudgeTooltip({
  offsetY = DEFAULT_OFFSET_Y,
}: Props = {}) {
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<Position | null>(null);
  const [visible, setVisible] = useState(false);

  const close = () => setVisible(false);

  useEffect(() => {
    if (!visible) return;

    const handleOutside = (e: Event) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || tooltipRef.current?.contains(t)) {
        return;
      }
      close();
    };

    // capture phase 로 동작해야 트리거의 stopPropagation 영향을 받지 않고
    // 다른 트리거 클릭 시에도 열려있던 툴팁이 닫힘
    document.addEventListener('click', handleOutside, true);
    document.addEventListener('scroll', close, true);
    // eslint-disable-next-line consistent-return
    return () => {
      document.removeEventListener('click', handleOutside, true);
      document.removeEventListener('scroll', close, true);
    };
  }, [visible]);

  const handleTriggerClick = (e: MouseEvent) => {
    stopEvent(e);
    if (visible) {
      close();
      return;
    }
    const trigger = triggerRef.current;
    if (!trigger) return;
    setPosition(computePosition(trigger, offsetY));
    setVisible(true);
  };

  return (
    <div ref={triggerRef} className="inline-flex">
      <InfoCircle10x10Icon onClick={handleTriggerClick} />
      {visible &&
        position &&
        createPortal(
          <MembershipStandardNudgeTooltipContent
            ref={tooltipRef}
            onClose={close}
            className="absolute inset-x-0 z-10 mx-auto"
            style={{
              top: position.top,
            }}
          />,
          document.body,
        )}
    </div>
  );
}
